import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import usePermissions from '../../../../hooks/usePermissions'

import FormDrawerWrapper from '../../../../features/components/FormDrawerWrapper'
import EditSectionsList from '../../../../features/components/Forms/EditForm/EditSectionsList'
import NameSectionPreview from '../../../ReusableFormSections/NameSection/NameSectionPreview'
import NameSection from './Sections/NameSection'
import BudgetSectionPreview from '../../../ReusableFormSections/reusableBudgetSections/GoogleCampaignBudgetDailySection/BudgetDailySectionPreview'
import BudgetSectionForm from '../../../ReusableFormSections/reusableBudgetSections/GoogleCampaignBudgetDailySection/BudgetDailySectionForm'
import DatesSectionPreview from './Sections/DatesSection/DatesSectionPreview'
import DatesSectionForm from './Sections/DatesSection/DatesSectionForm'
import ScheduleSectionPreview from './Sections/ScheduleSection/ScheduleSectionPreview'
import ScheduleSection from './Sections/ScheduleSection'
import TargetRoasSectionPreview from './Sections/TargetRoasSection/TargetRoasSectionPreview'
import TargetRoasSectionForm from './Sections/TargetRoasSection/TargetRoasSectionForm'
import GeoTargetingSectionPreview from './Sections/GeoTargetingSection/GeoTargetingSectionPreview'
import GeoTargetingSectionForm from './Sections/GeoTargetingSection/GeoTargetingSectionForm'
import LanguagesSectionPreview from './Sections/LanguagesSection/LanguagesSectionPreview'
import LanguagesSectionForm from './Sections/LanguagesSection/LanguagesSectionForm'
import CustomLocationsSectionPreview from './Sections/CustomLocationsSection/CustomLocationsSectionPreview'
import CustomLocationsSectionForm from './Sections/CustomLocationsSection/CustomLocationsSectionForm'

import useManageDrawer from '../../../../hooks/formHooks/useManageDrawer'
import useManageEditFormData from '../../../../hooks/formHooks/useManageEditFormData'
import { insertIf } from '../../../../helpers/common'

import {
  clearCampaign,
  clearCampaignAdScheduleCriterions,
  clearCampaignCriterions,
  getCampaign,
  getCampaignAdScheduleCriterions,
  getCampaignCriterions
} from '../../../../modules/actions/campaigns'
import { getGoogleLanguages } from '../../../../modules/actions/googleConstants'
import {
  campaignAdScheduleCriterionsWasLoadedSelector,
  campaignCriterionsWasLoadedSelector,
  campaignSelector,
  campaignWasLoadedSelector
} from '../../../../modules/selectors/campaigns'
import { selectedGoogleAdAccountSelector } from '../../../../modules/selectors/adAccounts'
import { googleLanguagesWasLoadedSelector } from '../../../../modules/selectors/googleConstants'

import { CAMPAIGN_GOOGLE_EDIT } from '../../../../constants/forms'
import {
  CAMPAIGN_BUDGET,
  CAMPAIGN_DATES,
  LOCATIONS,
  CUSTOM_LOCATIONS,
  NAME,
  TARGET_ROAS,
  AMOUNT_MICROS
} from '../../../Multiplatform/CampaignForms/fields'
import { LANGUAGES } from '../../../ReusableFormFields/LanguagesFields/fields'
import { SCHEDULE } from '../../../ReusableFormSections/ScheduleSection/fields'
import { GOOGLE_PLATFORM } from '../../../../constants/selectLists/platformList'
import { clearLocationLists, getLocationLists } from '../../../../modules/actions/location'
import { selectedPartnershipIdSelector } from '../../../../modules/selectors/partnerships'
import { locationListsWasLoadedSelector } from '../../../../modules/selectors/location'
import { CAMPAIGN_BUDGET_SECTION } from '../../../../constants/permissions'

// here defined initial open states for edit sections
const sectionsInitialOpenStates = {
  [NAME]: false,
  [CAMPAIGN_BUDGET]: false,
  [CAMPAIGN_DATES]: false,
  [SCHEDULE]: false,
  [TARGET_ROAS]: false,
  [LOCATIONS]: false,
  [CUSTOM_LOCATIONS]: false,
  [LANGUAGES]: false
}

const CampaignGoogleEdit = ({ shouldBeCleared }) => {
  const dispatch = useDispatch()

  const permissions = usePermissions()

  const campaign = useSelector(campaignSelector)
  const campaignWasLoaded = useSelector(campaignWasLoadedSelector)
  const campaignCriterionsWasLoaded = useSelector(campaignCriterionsWasLoadedSelector)
  const campaignAdScheduleCriterionsWasLoaded = useSelector(campaignAdScheduleCriterionsWasLoadedSelector)
  const languagesWasLoaded = useSelector(googleLanguagesWasLoadedSelector)
  const { id: selectedAdAccountId } = useSelector(selectedGoogleAdAccountSelector)

  const selectedPartnershipId = useSelector(selectedPartnershipIdSelector)
  const locationListWasLoaded = useSelector(locationListsWasLoadedSelector)

  const { id: campaignId } = campaign

  const budgetDaily = campaign?.[CAMPAIGN_BUDGET]?.[AMOUNT_MICROS]

  const isInitialDataLoading =
    !campaignWasLoaded ||
    !campaignCriterionsWasLoaded ||
    !campaignAdScheduleCriterionsWasLoaded ||
    !languagesWasLoaded ||
    !locationListWasLoaded

  const getCampaignDataHandler = useCallback(
    selectedEditItemId => {
      // Load campaign data only if user is on campaigns list page
      // if user is on campaign summary page - we already have campaign data in store
      if (!campaignWasLoaded) {
        dispatch(getCampaign({ account: selectedAdAccountId, id: selectedEditItemId }, GOOGLE_PLATFORM))
      }
    },
    [dispatch, campaignWasLoaded, selectedAdAccountId]
  )

  const clearCampaignDataHandler = useCallback(() => {
    if (shouldBeCleared) {
      dispatch(clearCampaign())
    }

    dispatch(clearCampaignCriterions())
    dispatch(clearCampaignAdScheduleCriterions())
    dispatch(clearLocationLists())
  }, [dispatch, shouldBeCleared])

  const { isFormOpen } = useManageDrawer({ formName: CAMPAIGN_GOOGLE_EDIT })

  useManageEditFormData({
    formName: CAMPAIGN_GOOGLE_EDIT,
    loadedDataId: campaign.id,
    getDataHandler: getCampaignDataHandler,
    clearDataHandler: clearCampaignDataHandler
  })

  const editSections = useMemo(
    () => [
      {
        sectionName: NAME,
        title: 'Name',
        PreviewComponent: <NameSectionPreview dataSelector={campaignSelector} />,
        FormComponent: <NameSection />
      },
      ...insertIf(permissions.can('update', CAMPAIGN_BUDGET_SECTION), {
        sectionName: CAMPAIGN_BUDGET,
        title: 'Budget',
        PreviewComponent: <BudgetSectionPreview budgetDaily={budgetDaily} />,
        FormComponent: <BudgetSectionForm budgetDaily={budgetDaily} />
      }),
      {
        sectionName: CAMPAIGN_DATES,
        title: 'Dates',
        PreviewComponent: <DatesSectionPreview />,
        FormComponent: <DatesSectionForm />
      },
      {
        sectionName: SCHEDULE,
        title: 'Schedule',
        PreviewComponent: <ScheduleSectionPreview />,
        FormComponent: <ScheduleSection />
      },
      {
        sectionName: TARGET_ROAS,
        title: 'Bidding',
        PreviewComponent: <TargetRoasSectionPreview />,
        FormComponent: <TargetRoasSectionForm />
      },
      {
        sectionName: LOCATIONS,
        title: 'Location Targeting',
        PreviewComponent: <GeoTargetingSectionPreview />,
        FormComponent: <GeoTargetingSectionForm />
      },
      {
        sectionName: CUSTOM_LOCATIONS,
        title: 'Custom Locations',
        PreviewComponent: <CustomLocationsSectionPreview />,
        FormComponent: <CustomLocationsSectionForm />
      },
      {
        sectionName: LANGUAGES,
        title: 'Languages',
        PreviewComponent: <LanguagesSectionPreview />,
        FormComponent: <LanguagesSectionForm />
      }
    ],
    [permissions, budgetDaily]
  )

  useEffect(() => {
    if (isFormOpen && campaignId) {
      // pre-load usual criterions data
      dispatch(
        getCampaignCriterions({
          account: selectedAdAccountId,
          campaign_id: campaignId
        })
      )

      // pre-load schedule criterions for schedule section
      dispatch(
        getCampaignAdScheduleCriterions({
          account: selectedAdAccountId,
          campaign: campaignId
        })
      )
    }
  }, [dispatch, isFormOpen, campaignId, selectedAdAccountId])

  useEffect(() => {
    // google language constants require account field, but can't be dependent on different accounts
    if (isFormOpen && !languagesWasLoaded) {
      dispatch(
        getGoogleLanguages({
          account: selectedAdAccountId
        })
      )
    }
  }, [dispatch, isFormOpen, languagesWasLoaded, selectedAdAccountId])

  useEffect(() => {
    if (isFormOpen) {
      dispatch(getLocationLists({ limit: 10, partnership: selectedPartnershipId }))
    }
  }, [dispatch, selectedPartnershipId, isFormOpen])

  return (
    <FormDrawerWrapper
      title="Edit the settings of your campaign"
      formName={CAMPAIGN_GOOGLE_EDIT}
      showOpenButton={false}
    >
      <EditSectionsList
        editSections={editSections}
        isEditDataLoading={isInitialDataLoading}
        editItemData={campaign}
        sectionsInitialOpenStates={sectionsInitialOpenStates}
      />
    </FormDrawerWrapper>
  )
}

export default CampaignGoogleEdit
